import React from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function PendingCardDetailsModal({ open, onClose, data }) {
  if (!open) return null;
  const { card, entries = [], users = [] } = data || {};
  const drawAt =
    card?.batchDrawAt?.toDate?.() ||
    (card?.batchDrawAt ? new Date(card.batchDrawAt) : null);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-slate-900 text-white w-full max-w-3xl rounded-2xl p-6 shadow-2xl"
          initial={{ scale: 0.95, opacity: 0.8 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0.8 }}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-sm text-slate-400">Card</div>
              <div className="font-bold text-lg">#{card?.id}</div>
              <div className="text-xs text-slate-400 mt-1">
                Draw @ {drawAt ? drawAt.toLocaleTimeString() : "-"}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Buyers summary */}
            <div className="bg-slate-800 rounded-xl p-4">
              <div className="font-semibold mb-2">Buyers</div>
              <div className="space-y-2 max-h-64 overflow-auto pr-2">
                {users.length === 0 && (
                  <div className="text-slate-400 text-sm">No buys yet.</div>
                )}
                {users.map((u) => (
                  <div key={u.uid} className="flex items-start justify-between">
                    <div className="truncate">
                      <div className="font-medium">{u.username}</div>
                      <div className="text-xs text-slate-400 break-words">
                        {u.numbers.sort((a, b) => a - b).join(", ")}
                      </div>
                    </div>
                    <div className="text-green-400 font-bold">{u.count}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Numbers list */}
            <div className="bg-slate-800 rounded-xl p-4">
              <div className="font-semibold mb-2">Numbers (who owns what)</div>
              <div className="grid grid-cols-5 gap-2 max-h-64 overflow-auto pr-2">
                {Array.from({ length: 100 }, (_, n) => {
                  const owner = entries.find((e) => e.number === n);
                  const label = owner ? owner.username : "";
                  return (
                    <div
                      key={n}
                      className={`rounded-lg px-2 py-2 text-center text-sm
                        ${
                          owner
                            ? "bg-green-500/80 text-black"
                            : "bg-slate-700 text-slate-300"
                        }`}
                      title={owner ? `${n} • ${owner.username}` : String(n)}
                    >
                      <div className="font-bold">{n}</div>
                      {owner && (
                        <div className="text-[10px] truncate">{label}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer stats */}
          <div className="mt-4 flex justify-between text-sm text-slate-400">
            <div>
              Picked:{" "}
              <span className="text-white font-semibold">{entries.length}</span>
              /100
            </div>
            {typeof card?.winningNumber === "number" && (
              <div>
                Winning number:{" "}
                <span className="text-yellow-300 font-bold">
                  {card.winningNumber}
                </span>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
